import { useEffect, useMemo, useState } from 'react';
import { VideoCameraIcon } from '@heroicons/react/24/outline';

import { connectApi, connectExtrasApi, formatMeetingCard, meetNowApi } from '../api/connectApi';
import type { ConnectPerson, ConnectThread } from '../api/connectApi';

// EdgeMeet Huddle (2026-08-24): Slack's huddle gesture with EdgeLex guts.
// A globally present 🎥 button opens a person/channel picker; starting a
// huddle creates a real EdgeMeet room (recording-capable), posts the
// meeting card INTO the Connect conversation — so the huddle is joinable
// by the room, announced where the work lives, and lands on the matter
// record when started in a matter channel — and opens the room. Nothing
// here is a new call stack: it is Meet-now made reachable from anywhere.

type Tab = 'people' | 'channels';

export function EdgeMeetHuddleLauncher() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('people');
  const [query, setQuery] = useState('');
  const [people, setPeople] = useState<ConnectPerson[]>([]);
  const [threads, setThreads] = useState<ConnectThread[]>([]);
  const [presenceOnline, setPresenceOnline] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<{ kind: 'person'; person: ConnectPerson } | { kind: 'thread'; thread: ConnectThread } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load channels + presence once per open; people search live.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setSelected(null);
    setError(null);
    connectApi.listThreads()
      .then((rows) => { if (!cancelled) setThreads(rows.filter((thread) => thread.type === 'matter' || thread.type === 'group')); })
      .catch(() => { if (!cancelled) setThreads([]); });
    connectExtrasApi.getPresence()
      .then((presence) => { if (!cancelled) setPresenceOnline(new Set(presence.online)); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open || tab !== 'people') return undefined;
    const normalized = query.replace(/^@+/, '').trim();
    let cancelled = false;
    const timer = window.setTimeout(() => {
      connectApi.listDirectory(normalized || 'a')
        .then((found) => { if (!cancelled) setPeople(found.slice(0, 10)); })
        .catch(() => { if (!cancelled) setPeople([]); });
    }, 200);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [open, query, tab]);

  const visibleThreads = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return threads
      .filter((thread) => !normalized
        || (thread.title ?? '').toLowerCase().includes(normalized)
        || thread.participants.some((person) => person.display_name.toLowerCase().includes(normalized)))
      .slice(0, 10);
  }, [query, threads]);

  const startHuddle = async () => {
    if (!selected || busy) return;
    setBusy(true);
    setError(null);
    try {
      let threadId: string;
      let title: string;
      let matterId: string | undefined;
      if (selected.kind === 'person') {
        const thread = await connectApi.createDirectThread(selected.person.id);
        threadId = thread.id;
        title = `Huddle — ${selected.person.display_name}`;
      } else {
        threadId = selected.thread.id;
        title = `Huddle — ${selected.thread.title || selected.thread.participants.map((p) => p.display_name).join(', ')}`;
        matterId = selected.thread.type === 'matter' && selected.thread.context?.type === 'matter'
          ? selected.thread.context.id
          : undefined;
      }
      const room = await meetNowApi.createRoom(title, { connectThreadId: threadId, matterId });
      await connectApi.sendMessage(threadId, formatMeetingCard(room.title, room.meetingId));
      setOpen(false);
      setQuery('');
      window.open(`/meet/${room.meetingId}/room`, '_blank');
    } catch (startError) {
      setError(startError instanceof Error ? startError.message : 'The huddle could not be started.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="edgemeet-huddle-button"
        title="Start a huddle (EdgeMeet)"
        aria-label="Start a huddle"
        onClick={() => setOpen(true)}
        className="rounded-md p-2 text-[var(--lex-text-secondary)] hover:bg-[var(--lex-surface-hover)] hover:text-[var(--lex-text-primary)]"
      >
        <VideoCameraIcon className="h-5 w-5" />
      </button>
      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Start a huddle"
          onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false); }}
        >
          <section data-testid="edgemeet-huddle-modal" className="w-full max-w-md rounded-xl border border-[var(--lex-border)] bg-[var(--lex-surface-card)] p-5 shadow-2xl">
            <div className="mb-1 flex items-start justify-between">
              <div>
                <h2 className="text-base font-semibold text-[var(--lex-text-primary)]">Start a huddle</h2>
                <p className="text-xs text-[var(--lex-text-tertiary)]">Find a person or conversation to huddle with — the meeting posts into the conversation.</p>
              </div>
              <button type="button" aria-label="Close" onClick={() => setOpen(false)} className="rounded-md px-1.5 text-[var(--lex-text-tertiary)] hover:text-[var(--lex-text-primary)]">✕</button>
            </div>
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name"
              className="mt-3 w-full rounded-lg border border-[var(--lex-border)] bg-[var(--lex-surface-base)] px-3 py-2 text-sm outline-none focus:border-[var(--lex-accent-primary)] focus:ring-2 focus:ring-[var(--lex-accent-ring)]"
            />
            <div className="mt-3 flex gap-4 border-b border-[var(--lex-border)] text-sm">
              {(['people', 'channels'] as const).map((candidate) => (
                <button
                  key={candidate}
                  type="button"
                  onClick={() => { setTab(candidate); setSelected(null); }}
                  className={`pb-1.5 font-medium ${tab === candidate ? 'border-b-2 border-[var(--lex-accent-primary)] text-[var(--lex-text-primary)]' : 'text-[var(--lex-text-tertiary)]'}`}
                >
                  {candidate === 'people' ? 'People' : 'Conversations'}
                </button>
              ))}
            </div>
            <ul className="mt-2 max-h-64 overflow-y-auto">
              {tab === 'people' && people.map((person) => (
                <li key={person.id}>
                  <button
                    type="button"
                    onClick={() => setSelected({ kind: 'person', person })}
                    className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-[var(--lex-surface-hover)] ${selected?.kind === 'person' && selected.person.id === person.id ? 'bg-[var(--lex-surface-hover)]' : ''}`}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${presenceOnline.has(person.id) ? 'bg-emerald-500' : 'bg-[var(--lex-border-strong)]'}`} aria-hidden="true" />
                      <span className="truncate font-medium text-[var(--lex-text-primary)]">{person.display_name}</span>
                    </span>
                    <span className="truncate text-xs text-[var(--lex-text-tertiary)]">{person.email ?? ''}</span>
                  </button>
                </li>
              ))}
              {tab === 'channels' && visibleThreads.map((thread) => (
                <li key={thread.id}>
                  <button
                    type="button"
                    onClick={() => setSelected({ kind: 'thread', thread })}
                    className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-[var(--lex-surface-hover)] ${selected?.kind === 'thread' && selected.thread.id === thread.id ? 'bg-[var(--lex-surface-hover)]' : ''}`}
                  >
                    <span className="truncate font-medium text-[var(--lex-text-primary)]">
                      {thread.type === 'matter' ? '⚖️ ' : '👥 '}
                      {thread.title || thread.participants.map((p) => p.display_name).join(', ')}
                    </span>
                    <span className="text-xs text-[var(--lex-text-tertiary)]">{thread.participants.length} people</span>
                  </button>
                </li>
              ))}
              {tab === 'people' && people.length === 0 && <li className="px-3 py-2 text-xs text-[var(--lex-text-tertiary)]">No matches.</li>}
              {tab === 'channels' && visibleThreads.length === 0 && <li className="px-3 py-2 text-xs text-[var(--lex-text-tertiary)]">No conversations match.</li>}
            </ul>
            {error && <p className="mt-2 rounded-md border border-[var(--lex-border)] bg-[var(--lex-surface-muted)] px-3 py-2 text-xs text-[var(--lex-text-secondary)]">{error}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded-lg border border-[var(--lex-border)] px-4 py-2 text-sm font-medium text-[var(--lex-text-secondary)] hover:bg-[var(--lex-surface-hover)]">Cancel</button>
              <button
                type="button"
                disabled={!selected || busy}
                onClick={() => void startHuddle()}
                className="rounded-lg bg-[var(--lex-accent-primary)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy ? 'Starting…' : 'Start a huddle'}
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
